import { request } from '../utility/apiClients';
import { APIS_METHOD, APIS_ENDPOINTS } from '../resources/api-constants';
import { renderCustomCloseButton } from '../utility/helpers';
import { toast } from 'react-toastify';

export interface Plan {
  id: number;
  name: string;
  no_of_downloads: number;
  no_of_job_creation: number;
  no_of_profile_views: number;
  is_active: boolean;
  is_free: boolean;
  // Custom properties we add for UI
  price?: string | null;
  is_popular?: boolean;
}

/**
 * Fetch all available pricing plans
 * @returns Promise with plans data
 */
export const fetchPricingPlans = async (): Promise<Plan[]> => {
  try {
    const response = await request(
      APIS_METHOD.GET,
      APIS_ENDPOINTS.TIER_DETAILS,
      {},
      false
    );
    
    if (!response || !response.data) {
      throw new Error('No tier details received');
    }
    
    // The API returns { detail: null, data: [...] }
    const apiData = response.data.data || [];
    
    // Use all plans directly from the API without filtering
    const plans = apiData.map((plan: any) => {
      // Set price based on whether it's free or not
      const price = plan.name === 'SBR2TH Premium' ? '$99/month' : '';
      
      return {
        ...plan,
        price,
        // Mark the first premium plan as popular (if it exists)
        is_popular: !plan.is_free && plan.name === 'SRB2TH Premium'
      };
    });
    
    return plans;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to fetch tier details';
    toast.error(errorMessage, { closeButton: renderCustomCloseButton() });
    throw new Error(errorMessage);
  }
};

/**
 * Select a plan for the user
 * @param planId - ID of the plan to select
 * @returns Promise with selection result
 */
export const selectPlan = async (planId: number): Promise<any> => {
  try {
    // The payload structure is based on the backend API requirements
    const payload = {
      tier_id: planId,
      // Add any additional required fields here
    };
    
    const response = await request(
      APIS_METHOD.POST,
      APIS_ENDPOINTS.SELECT_PLAN,
      payload,
      true
    );
    
    if (!response) {
      throw new Error('Failed to select plan');
    }
    
    return response.data;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to select plan';
    toast.error(errorMessage, { closeButton: renderCustomCloseButton() });
    throw new Error(errorMessage);
  }
};

/**
 * Contact sales with user details and plan information
 * @param userDetails - Complete user profile data
 * @param currentPlan - User's current plan details
 * @param requestedPlan - Plan the user is requesting
 * @returns Promise with contact sales result
 */
export const contactSales = async (
  userDetails: any,
  currentPlan: any,
  requestedPlan: any
): Promise<any> => {
  try {
    const payload = {
      user_details: {
        user_id: userDetails.user?.id,
        company_id: userDetails.id,
        company_name: userDetails.company_name,
        email: userDetails.user?.email,
        full_name: userDetails.user?.full_name,
        phone_number: userDetails.user?.phone_number,
        website_link: userDetails.website_link,
        industry_type: userDetails.industry_type,
        annual_revenue: userDetails.annual_revenue,
        company_size: userDetails.company_size,
        office_address: userDetails.office_address
      },
      current_plan: {
        id: currentPlan?.id,
        name: currentPlan?.name,
        no_of_downloads: currentPlan?.no_of_downloads,
        no_of_job_creation: currentPlan?.no_of_job_creation,
        no_of_profile_views: currentPlan?.no_of_profile_views
      },
      requested_plan: {
        id: requestedPlan?.id,
        name: requestedPlan?.name,
        no_of_downloads: requestedPlan?.no_of_downloads,
        no_of_job_creation: requestedPlan?.no_of_job_creation,
        no_of_profile_views: requestedPlan?.no_of_profile_views,
        is_active: requestedPlan?.is_active,
        is_free: requestedPlan?.is_free,
        price: requestedPlan?.price
      },
      contact_timestamp: new Date().toISOString()
    };
    
    const response = await request(
      APIS_METHOD.POST,
      APIS_ENDPOINTS.CONTACT_SALES,
      payload,
      true
    );
    
    if (!response) {
      throw new Error('Failed to contact sales');
    }
    
    return response.data;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to contact sales';
    toast.error(errorMessage, { closeButton: renderCustomCloseButton() });
    throw new Error(errorMessage);
  }
};

/**
 * Signup plan selection - user selects plan during signup
 * Always assigns Free Tier but notifies sales team of requested plan
 * @param requestedPlanId - ID of the plan user wants
 * @returns Promise with assignment result and redirect flag
 */
export const signupPlanSelection = async (requestedPlanId: number): Promise<any> => {
  try {
    const payload = {
      requested_plan_id: requestedPlanId
    };
    
    const response = await request(
      APIS_METHOD.POST,
      APIS_ENDPOINTS.SIGNUP_PLAN_SELECTION,
      payload,
      true
    );
    
    if (!response) {
      throw new Error('Failed to select plan');
    }
    
    return response.data;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to select plan';
    toast.error(errorMessage, { closeButton: renderCustomCloseButton() });
    throw new Error(errorMessage);
  }
};
